// French translation maps for the permitting block. The engine emits
// authority + status strings in English; when the buyer's locale is FR
// we map to natural-language equivalents while preserving proper
// institutional names (NERC, EPRA, KPLC, TANESCO, etc.) verbatim.
// Any string not in the map falls back to the original English so a
// newly-added permit row is never silently blanked.

const STATUS_FR = {
  "Advisory": "Consultatif",
  "Applies < 15 MW": "S'applique < 15 MW",
  "Case-by-case": "Au cas par cas",
  "Indicative — confirm locally": "Indicatif — à confirmer localement",
  "Indicative": "Indicatif",
  "No licence required": "Aucune licence requise",
  "Notification": "Notification",
  "Often licence-exempt": "Souvent exempté de licence",
  "Registration / no licence": "Enregistrement / pas de licence",
  "Registration only": "Enregistrement seulement",
  "Required (sale to bulk customers or ECG/NEDCo)": "Requis (vente aux gros clients ou ECG/NEDCo)",
  "Required above 1 MW": "Requis au-delà de 1 MW",
  "Required for larger sites": "Requis pour les grands sites",
  "Required if exporting": "Requis en cas d'export vers le réseau",
  "Required if selling to end users": "Requis pour la vente aux utilisateurs finaux",
  "Required": "Requis",
  "To be confirmed": "À confirmer",
};

const AUTHORITY_FR = {
  "County Government": "Gouvernement de comté",
  "EPRA (Energy & Petroleum Regulatory Authority)": "EPRA (Autorité de régulation de l'énergie et du pétrole)",
  "EWURA (Energy & Water Utilities Regulatory Authority)": "EWURA (Autorité de régulation des services d'énergie et d'eau)",
  "Environment agency": "Agence environnementale",
  "Environmental Protection Agency (EPA Ghana)": "Agence de protection de l'environnement (EPA Ghana)",
  "Federal Ministry of Environment": "Ministère fédéral de l'Environnement",
  "Local authority": "Autorité locale",
  "Ministry of Energy & Petroleum": "Ministère de l'Énergie & du Pétrole",
  "Ministry of Lands / Village Council": "Ministère des Terres / Conseil villageois",
  "NEMA (National Environment Management Authority)": "NEMA (Autorité nationale de gestion de l'environnement)",
  "NEMC (National Environment Management Council)": "NEMC (Conseil national de gestion de l'environnement)",
  "NERC (Nigerian Electricity Regulatory Commission)": "NERC (Commission de régulation de l'électricité du Nigeria)",
  "National electricity regulator": "Régulateur national de l'électricité",
  "National environment agency": "Agence nationale de l'environnement",
  "National utility": "Compagnie nationale d'électricité",
  "State ministries": "Ministères de l'État fédéré",
};

// A3 (30 Oct) — display labels only; the configurator keeps returning the stored values.
export const REGIME_EN = {
  "Detailed (priority country)": "Detailed (country-specific)",
  "Priority (West Africa) — indicative": "Indicative (general checklist)",
  "Indicative (non-priority country)": "Indicative (general checklist)",
};
export const REGIME_FR = {
  "Detailed (priority country)": "Détaillé (propre au pays)",
  "Priority (West Africa) — indicative": "Indicatif (liste de contrôle générale)",
  "Indicative (non-priority country)": "Indicatif (liste de contrôle générale)",
};

export const regimeLabel = (regime, locale) => (locale === "fr" ? REGIME_FR : REGIME_EN)[regime] ?? regime;

// Item titles from the engine — most contain thresholds/technical
// phrasing. We translate the common ones and leave rarities in English
// (they'll still read cleanly next to a French status pill / regulator).
const ITEM_FR = {
  "Self-generation < 1 MW for own use": "Autoproduction < 1 MW pour usage propre",
  "Captive Generation Permit": "Permis de production captive",
  "Mini-grid Permit (100 kW – 1 MW)": "Permis mini-réseau (100 kW – 1 MW)",
  "Generation Licence (for sale / on-grid)": "Licence de production (vente / réseau)",
  "Environmental Impact Assessment": "Étude d'impact environnemental",
  "State/Local land-use & building approval": "Approbation d'usage des sols & construction (État/local)",
  "Generation Licence (captive > 1 MW)": "Licence de production (captive > 1 MW)",
  "Generation Licence (commercial)": "Licence de production (commerciale)",
  "Feed-in Tariff / auction eligibility (renewables)": "Tarif d'achat / éligibilité aux enchères (renouvelables)",
  "Mini-grid permit": "Permis mini-réseau",
  "Environmental & Social Impact Assessment": "Étude d'impact environnemental et social",
  "County government construction & land-use approval": "Approbation construction & usage des sols (comté)",
  "Grid connection & wheeling agreement": "Accord de raccordement & de transit sur le réseau",
  "Captive generation licence (≥ 1 MW)": "Licence de production captive (≥ 1 MW)",
  "Generation Licence + PPA with TANESCO": "Licence de production + PPA avec TANESCO",
  "Small Power Producer (SPP) framework": "Cadre Small Power Producer (SPP)",
  "Mini-grid / isolated grid permit": "Permis mini-réseau / réseau isolé",
  "Land title / right-of-occupancy": "Titre foncier / droit d'occupation",
  "Environmental permit": "Permis environnemental",
  "Grid connection agreement (if any export/import)": "Accord de raccordement (si export/import)",
  "Generation licence / exemption threshold check": "Licence de production / vérification du seuil d'exemption",
  "Environmental & social permit": "Permis environnemental et social",
  "Site approval / land use": "Approbation du site / usage des sols",
  "Fuel/emissions permit (if thermal)": "Permis carburant/émissions (si thermique)",
  "Grid connection agreement (if applicable)": "Accord de raccordement (le cas échéant)",
  "Self-generation for own use (typical threshold: small scale)": "Autoproduction pour usage propre (seuil typique : petite échelle)",
  "Self-generation (small scale, <100 kW)": "Autoproduction (petite échelle, <100 kW)",
  "Self-generation permit (100 kW – 1 MW)": "Permis d'autoproduction (100 kW – 1 MW)",
  "Self-generation licence (≥ 1 MW)": "Licence d'autoproduction (≥ 1 MW)",
  "Wholesale supply licence": "Licence de fourniture en gros",
  "Bulk customer licence (if selling to eligible large consumer)": "Licence gros client (vente à un consommateur éligible)",
  "Tariff approval": "Approbation tarifaire",
};

// Item strings interpolated with dynamic values (e.g. "Category by
// capacity / ownership (500 kW · IPP)"). We match on the leading
// prefix and preserve the parenthetical tail verbatim.
const ITEM_PREFIX_FR = [
  {
    en: "Category by capacity / ownership",
    fr: "Catégorie selon capacité / mode de détention",
  },
];

const localiseItem = (label) => {
  if (!label) return label;
  if (ITEM_FR[label]) return ITEM_FR[label];
  for (const p of ITEM_PREFIX_FR) {
    if (label.startsWith(p.en)) return p.fr + label.slice(p.en.length);
  }
  return label;
};

const NOTES_FR = {
  "Act 541 self-generation category. Confirm exact threshold with EC Licence & Permit Manual.": "Catégorie autoproduction de l'Act 541. Confirmez le seuil exact dans le manuel des licences et permis de l'EC.",
  "Act 541 self-generation for own consumption at scale.": "Autoproduction à grande échelle pour usage propre au titre de l'Act 541.",
  "Act 541 wholesale supply category. PPA with off-taker required.": "Catégorie fourniture en gros de l'Act 541. Contrat d'achat (PPA) avec l'acheteur requis.",
  "Any capacity sold to third parties or exported to grid.": "Toute capacité vendue à des tiers ou injectée sur le réseau.",
  "Bulk-customer framework under Act 541.": "Cadre des gros consommateurs au titre de l'Act 541.",
  "Confirm exact classification with an engineer.": "Confirmez la classification exacte avec un ingénieur.",
  "Devolved planning powers.": "Compétences d'aménagement décentralisées.",
  "EIA certificate.": "Certificat d'EIE.",
  "EIA licence for construction.": "Licence d'EIE pour la construction.",
  "EIA screening; permit required above thresholds.": "Examen préalable d'EIE\u00a0; permis requis au-delà des seuils.",
  "For > 1 MW own consumption, no third-party sale.": "Pour une autoconsommation > 1\u00a0MW, sans vente à des tiers.",
  "For own-consumption plant of 1 MW or more.": "Pour une centrale d'autoconsommation de 1\u00a0MW ou plus.",
  "Interconnection studies.": "Études de raccordement.",
  "Isolated or interconnected mini-grid framework.": "Cadre des mini-réseaux isolés ou interconnectés.",
  "Kenya Mini-grid Regulations 2022.": "Réglementation kényane des mini-réseaux de 2022.",
  "Own use; no third-party sale. Notify Energy Commission.": "Usage propre\u00a0; pas de vente à des tiers. Informer l'Energy Commission.",
  "Own-consumption only, no third-party sale.": "Autoconsommation uniquement, sans vente à des tiers.",
  "PURC sets/approves electricity tariffs.": "La PURC fixe et approuve les tarifs d'électricité.",
  "Plus Power Purchase Agreement with Kenya Power (KPLC) or a third party.": "Plus un contrat d'achat d'électricité avec Kenya Power (KPLC) ou un tiers.",
  "Register the facility; no full licence for own-use.": "Enregistrez l'installation\u00a0; pas de licence complète pour l'usage propre.",
  "Register the plant; no licence for pure own-use.": "Enregistrez la centrale\u00a0; pas de licence pour un usage strictement propre.",
  "Required for any generation ≥ 1 MW even if own-consumption.": "Requise pour toute production ≥ 1\u00a0MW, même en autoconsommation.",
  "Rural electrification / mini-grid framework.": "Cadre de l'électrification rurale / des mini-réseaux.",
  "Rural energy mini-grid framework (REA-supported).": "Cadre des mini-réseaux d'énergie rurale (soutenu par la REA).",
  "Screening at >5 MW or sensitive site.": "Examen préalable au-delà de 5\u00a0MW ou sur site sensible.",
  "Setback, fire safety, noise (gensets).": "Distances de recul, sécurité incendie, bruit (groupes).",
  "Solar/wind projects may qualify under FiT policy or competitive procurement.": "Les projets solaires/éoliens peuvent être éligibles au tarif d'achat (FiT) ou aux appels d'offres.",
  "Standardised Power Purchase Agreement for renewables (SPPA) available below 15 MW.": "Contrat d'achat standardisé pour les renouvelables (SPPA) disponible en dessous de 15\u00a0MW.",
  "Standardised tariff and PPA for small renewable IPPs.": "Tarif et PPA standardisés pour les petits IPP renouvelables.",
  "Standby plants intended to support a captive user.": "Centrales de secours destinées à un utilisateur en autoproduction.",
  "Threshold varies — confirm.": "Le seuil varie — à confirmer.",
  "Village/general land requires derivative right for private use.": "Les terres villageoises/générales exigent un droit dérivé pour un usage privé.",
};

const nbUnit = (s) => (typeof s === "string" ? s.replace(/(\d) (kW|MW|kWh)\b/g, "$1\u00a0$2") : s);

/** Localise a permitting block. EN only relabels the regime. */
export const localisePermitting = (permits, locale) => {
  if (!permits) return permits;
  if (locale !== "fr") return { ...permits, regime: regimeLabel(permits.regime, locale) };
  return {
    ...permits,
    regime: regimeLabel(permits.regime, locale),
    items: (permits.items || []).map((it) => ({
      ...it,
      item: nbUnit(localiseItem(it.item)),
      status: nbUnit(STATUS_FR[it.status] ?? it.status),
      notes: nbUnit(NOTES_FR[it.notes] ?? it.notes),
      authority: AUTHORITY_FR[it.authority] ?? it.authority,
    })),
  };
};
